import { LoadingIndicator } from "@laerdal/life-react-components";
import posthog from "posthog-js";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import ProductListItem from "../../../commonComponents/ProductListItem";
import ProductApi from "../../../services/api/ProductApi";
import { ProductCategoryDto } from "../../../model/dto/productCategoryDto";
import { ProductListItemDto } from "../../../model/dto/productListItemDto";
import {
  CategoriesSection,
  CategoriesGrid,
  CategoryProducts,
  CategoryProductsTitle,
  ProductsGrid,
  SectionTitle,
} from "./CaseAssetsSelectPageComponents";

const CategoryItemWrapper = styled.div<{ $active: boolean }>`
  border-radius: 8px;
  ${props => props.$active && `
    > div {
      background: var(--Primary-100-tint, #D4E9F2);
      border-color: var(--Primary-100-tint, #D4E9F2);
    }
  `}
`;

interface CategoryCardItem {
  key: string;
  name: string;
  imageUrl: string;
  linkNames: string[];
}

interface CategoriesBrowserProps {
  searchTermDebounced: string | null;
  onProductSelect: (
    productName: string,
    productLineCode?: string,
    categoryLink?: string,
    productLink?: string,
  ) => void;
}

const CategoriesBrowser = ({ searchTermDebounced, onProductSelect }: CategoriesBrowserProps) => {
  const { t } = useTranslation("cases");

  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(true);
  const [categories, setCategories] = useState<CategoryCardItem[]>([]);
  const [selectedCategoryKey, setSelectedCategoryKey] = useState<string | null>(null);
  const [categoryProductsLoading, setCategoryProductsLoading] = useState<boolean>(false);
  const [categoryProducts, setCategoryProducts] = useState<ProductListItemDto[]>([]);

  useEffect(() => {
    let isActive = true;
    setCategoriesLoading(true);
    ProductApi.GetProductCategories()
      .then((data) => {
        if (!isActive || !data) return;
        const items = buildCategoryCards(data);
        setCategories(items);
      })
      .finally(() => {
        if (isActive) {
          setCategoriesLoading(false);
        }
      });
    return () => {
      isActive = false;
    };
  }, []);

  const selectedCategory = useMemo(() => {
    return categories.find((item) => item.key === selectedCategoryKey) || null;
  }, [categories, selectedCategoryKey]);

  useEffect(() => {
    if (!selectedCategory) {
      setCategoryProducts([]);
      return;
    }

    let isActive = true;
    setCategoryProductsLoading(true);
    const loadCategory = async () => {
      if (selectedCategory.linkNames.length === 0) {
        setCategoryProducts([]);
        return;
      }

      if (selectedCategory.linkNames.length === 1) {
        const data = await ProductApi.GetProductCategory(selectedCategory.linkNames[0]);
        if (!isActive || !data) return;
        setCategoryProducts(data.products || []);
        return;
      }

      const categoriesData = await Promise.all(
        selectedCategory.linkNames.map((linkName) => ProductApi.GetProductCategory(linkName))
      );
      if (!isActive) return;

      const combined = categoriesData
        .filter(Boolean)
        .flatMap((item) => item?.products || []);

      const unique = new Map<string, ProductListItemDto>();
      combined.forEach((product) => {
        if (product?.link && !unique.has(product.link)) {
          unique.set(product.link, product);
        }
      });

      const merged = Array.from(unique.values());
      setCategoryProducts(merged);
    };

    loadCategory().finally(() => {
      if (isActive) {
        setCategoryProductsLoading(false);
      }
    });

    return () => {
      isActive = false;
    };
  }, [selectedCategory]);

  return (
    <CategoriesSection>
      <SectionTitle>{t("Browse Categories")}</SectionTitle>
      {categoriesLoading && <LoadingIndicator />}
      {!categoriesLoading && (
        <>
          <CategoriesGrid>
            {categories.map((category) => (
              <CategoryItemWrapper key={category.key} $active={category.key === selectedCategoryKey}>
                <ProductListItem
                  title={t(category.name)}
                  imageUrl={category.imageUrl || "/assets/ProductPageDefault.png"}
                  bold
                  onClick={() => {
                    posthog?.capture('SUPP-Casedeflection-CategorySelected', {
                      categoryName: category.name,
                    });
                    setSelectedCategoryKey((current) => current === category.key ? null : category.key);
                  }}
                />
              </CategoryItemWrapper>
            ))}
          </CategoriesGrid>

          {selectedCategory && (
            <CategoryProducts>
              <CategoryProductsTitle>
                {t(selectedCategory.name)}
              </CategoryProductsTitle>
              {categoryProductsLoading && <LoadingIndicator />}
              {!categoryProductsLoading && categoryProducts.length > 0 && (
                <ProductsGrid>
                  {categoryProducts.map((product) => (
                    <ProductListItem
                      key={product.link}
                      title={product.name}
                      imageUrl={product.mainImageUrl || "/assets/ProductPageDefault.png"}
                      titleSize={16}
                      
                      onClick={() => {
                        posthog?.capture?.("SearchProductClick", {
                          productLine: product.link,
                          searchType: "caseCreation",
                          searchTerm: searchTermDebounced
                        });
                        posthog?.capture('SUPP-Casedeflection-AssetSelected', {
                          productLineName: product.name,
                          productLineCode: product.link,
                        });
                        onProductSelect(
                          product.name,
                          product.link,
                          selectedCategoryKey || undefined,
                          product.link
                        );
                      }}
                    />
                  ))}
                </ProductsGrid>
              )}
            </CategoryProducts>
          )}
        </>
      )}
    </CategoriesSection>
  );
};

const buildCategoryCards = (data: ProductCategoryDto[]): CategoryCardItem[] => {
  const categories = [...data];
  const used = new Set<string>();
  const cards: CategoryCardItem[] = [];

  categories.forEach((category) => {
    if (used.has(category.linkName)) return;
    used.add(category.linkName);
    cards.push({
      key: category.linkName,
      name: category.name,
      imageUrl: category.mainImageUrl,
      linkNames: [category.linkName]
    });
  });

  return cards;
};

export default CategoriesBrowser;
